
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  FaTicketAlt, 
  FaChartBar,
  FaCalendarAlt,
} from 'react-icons/fa';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer, 
  LineChart, 
  Line 
} from 'recharts';
import { fetchMyEvents, getEventAttendanceStats } from '../../../../utils/api/organiserApi';
import GlobalHeader from '../../../../components/GlobalHeader.jsx';
import '../css/Analytics.css';

// Custom Tooltip component for charts
const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="analytics-tooltip">
        <p className="analytics-tooltip-label">{label}</p>
        {payload.map((entry, index) => (
          <p
            key={`tooltip-${index}`}
            className="analytics-tooltip-item"
            style={{ color: entry.color }}
          >
            {entry.name}: {entry.value}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

const Analytics = () => {
  const navigate = useNavigate();
  const currentDate = new Date();
  const currentMonth = currentDate.toLocaleString('default', { month: 'long' });
  const currentYear = currentDate.getFullYear().toString();

  // State for each chart type
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [attendanceData, setAttendanceData] = useState({
    chartData: [{
      name: 'Tickets',
      Purchased: 0,
      Attended: 0
    }],
    attendanceRate: 0
  });
  const [loadingStats, setLoadingStats] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [ticketData, setTicketData] = useState([
    { event: 'No Data', ticketsSold: 1 }
  ]);
  const [metrics, setMetrics] = useState({ totalEvents: 0, totalAttendance: 0, totalTickets: 0 });

  // No inline styles; layout and visuals are handled in Analytics.css

  // Generate dynamic years (2022 to current year)
  const years = Array.from(
    { length: currentYear - 2021 },
    (_, i) => (2022 + i).toString()
  );

  // Handle back button click
  const handleBack = () => {
    navigate("/organiser-dash");
  };
  
  // Handle logout
  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("userId");
    localStorage.removeItem("user");
    navigate("/");
  };

  // Fetch events on component mount
  useEffect(() => {
    const fetchEvents = async () => {
      try {
        setLoading(true);
        setError('');
        
        console.log('[Analytics] 📊 Fetching — API: fetchMyEvents()');
        const fetchedEvents = await fetchMyEvents();
        console.log('[Analytics] ✅ Events loaded:', {
          columns: 'event_id/_id, event_name/Name/name, start_date, end_date, location',
          count: fetchedEvents?.length,
          data: fetchedEvents,
        });
        
        if (!Array.isArray(fetchedEvents)) {
          throw new Error('Invalid events data received');
        }

        // Ensure we have a valid array of events with proper structure
        const validEvents = fetchedEvents.filter(event => {
          const isValid = event && 
                         typeof event === 'object' && 
                         (event._id || event.event_id || event.id);
          if (!isValid) {
            console.warn('Skipping invalid event:', event);
          }
          return isValid;
        });

        console.log('[Analytics] 🔍 Valid events after filter:', { count: validEvents.length });
        
        const mappedEvents = validEvents.map(event => ({
          ...event,
          _id: event._id || event.event_id || event.id || parseInt(event.event_id) || parseInt(event.id),
          title: event.event_name || event.Name || event.name || 'Untitled Event'
        }));

        setEvents(mappedEvents);
        
        // Set the first event as selected if available
        if (mappedEvents.length > 0) {
          setSelectedEvent(mappedEvents[0]._id);
        }
      } catch (err) {
        console.error('Error in fetchEvents:', err);
        setError('Failed to load events. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchEvents();
  }, []);

  // Fetch attendance stats when selected event changes
  const fetchAttendanceStats = useCallback(async () => {
    if (!selectedEvent) {
      setAttendanceData({
        chartData: [{ name: 'Tickets', Purchased: 0, Attended: 0 }],
        attendanceRate: 0
      });
      setTicketData([{ event: 'No Event Selected', ticketsSold: 0 }]);
      return;
    }
    
    try {
      setLoadingStats(true);
      console.log('[Analytics] 📈 Fetching attendance — API: getEventAttendanceStats()', { selectedEvent });
      const stats = await getEventAttendanceStats(selectedEvent);
      console.log('[Analytics] ✅ Attendance stats loaded:', {
        columns: 'totalPurchased, totalAttended',
        formula: 'attendanceRate = Math.round((totalAttended / totalPurchased) * 100)',
        data: stats,
      });
      
      // Ensure we have valid data before updating state
      if (stats) {
        const chartData = [{
          name: 'Tickets',
          Purchased: stats.totalPurchased || 0,
          Attended: stats.totalAttended || 0
        }];

        const attendanceRate = stats.totalPurchased > 0
          ? Math.round((stats.totalAttended / stats.totalPurchased) * 100)
          : 0;

        setAttendanceData({ chartData, attendanceRate });

        const eventTitle = events.find(e => String(e._id) === String(selectedEvent))?.title || 'Selected Event';
        setTicketData([{ event: eventTitle, ticketsSold: stats.totalPurchased || 0 }]);
      }
    } catch (err) {
      console.error('Error fetching attendance stats:', err);
      setError('Failed to load attendance statistics');
      // Reset to default state on error
      setAttendanceData({
        chartData: [{
          name: 'Tickets',
          Purchased: 0,
          Attended: 0
        }],
        attendanceRate: 0
      });
    } finally {
      setLoadingStats(false);
    }
  }, [selectedEvent]);

  useEffect(() => {
    fetchAttendanceStats();
  }, [fetchAttendanceStats]);

  // Render loading state
  if (loading && events.length === 0) {
    return (
      <div className="analytics-container">
        <GlobalHeader
          title="Analytics Dashboard"
          subtitle="Track your event performance and attendance"
          backTo="/organiser-dash"
          logoutTo="/"
          onLogout={handleLogout}
        />
        <div className="loading-container">
          <div className="loading-spinner" />
          <p>Loading analytics...</p>
        </div>
      </div>
    );
  }

  // Render error state
  if (error) {
    return (
      <div className="analytics-container">
        <GlobalHeader
          title="Analytics Dashboard"
          subtitle="Track your event performance and attendance"
          backTo="/organiser-dash"
          logoutTo="/"
          onLogout={handleLogout}
        />
        <div className="error-message">Error: {error}</div>
      </div>
    );
  }

  // Render no events state
  if (events.length === 0) {
    return (
      <div className="analytics-container">
        <GlobalHeader
          title="Analytics Dashboard"
          subtitle="Track your event performance and attendance"
          backTo="/organiser-dash"
          logoutTo="/"
          onLogout={handleLogout}
        />
        <div className="no-events">
          <FaCalendarAlt className="no-events-icon" />
          <h2>No events found</h2>
          <p>Get started by creating your first event.</p>
          <button
            className="primary-button"
            onClick={() => navigate('/organiser/events/new')}
          >
            Create Event
          </button>
        </div>
      </div>
    );
  }

  // Render the main analytics dashboard
  return (
    <div className="analytics-container">
      <GlobalHeader
        title="Analytics Dashboard"
        subtitle="Track your event performance and attendance"
        backTo="/organiser-dash"
        logoutTo="/"
        onLogout={handleLogout}
      />

      {/* Combined Filter Controls and Metrics */}
      <div className="analytics-filter-controls">
        <div className="analytics-filter-section">
          <select
            className="event-selector"
            value={selectedEvent}
            onChange={(e) => setSelectedEvent(e.target.value)}
          >
            <option key="select-default" value="">Select an Event</option>
            {events.map(event => {
              console.log('[Analytics] 🗂️ Rendering event option:', { id: event._id, title: event.title || event.event_name || 'Untitled Event' });
              return (
                <option 
                  key={`event-${event._id}`}
                  value={event._id}
                >
                  {event.title || event.event_name || 'Untitled Event'}
                </option>
              );
            })}
          </select>
        </div>
        
        <div className="analytics-metrics-section">
          <div className="analytics-metric-item">
            <div className="analytics-metric-value">{events.length}</div>
            <div className="analytics-metric-label">Total Events</div>
          </div>
          <div className="analytics-metric-item">
            <div className="analytics-metric-value">
              {attendanceData?.attendanceRate ?? 0}%
            </div>
            <div className="analytics-metric-label">Attendance Rate</div>
          </div>
          <div className="analytics-metric-item">
            <div className="analytics-metric-value">
              {attendanceData?.chartData?.[0]?.Purchased ?? 0}
            </div>
            <div className="analytics-metric-label">Total Tickets</div>
          </div>
          <div className="analytics-metric-item">
            <div className="analytics-metric-value">
              {attendanceData?.chartData?.[0]?.Attended ?? 0}
            </div>
            <div className="analytics-metric-label">Attended</div>
          </div>
        </div>
      </div>

      {/* Dashboard Grid */}
      <div className="dashboard-grid">
        {/* Attendance Bar Chart Card */}
        <div className="chart-card">
          <h3>
            <FaChartBar style={{ marginRight: '10px' }} />
            Attendance Overview
          </h3>
          
          {loadingStats ? (
            <div className="loading-container">
              <div className="loading-spinner"></div>
              <p>Loading attendance data...</p>
            </div>
          ) : attendanceData?.chartData?.length > 0 ? (
            <div>
              <div className="chart-wrapper">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={attendanceData.chartData}
                    margin={{
                      top: 20,
                      right: 30,
                      left: 20,
                      bottom: 5,
                    }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend />
                    <Bar dataKey="Purchased" fill="#8884d8" name="Tickets Purchased" />
                    <Bar dataKey="Attended" fill="#82ca9d" name="Attendees" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              
              <div className="analytics-metrics-container">
                <div className="analytics-metric-card">
                  <div className="analytics-metric-value">
                    {attendanceData?.attendanceRate ?? 0}%
                  </div>
                  <div className="analytics-metric-label">Attendance Rate</div>
                </div>
                <div className="analytics-metric-card">
                  <div className="analytics-metric-value">
                    {attendanceData?.chartData?.[0]?.Purchased ?? 0}
                  </div>
                  <div className="analytics-metric-label">Total Tickets</div>
                </div>
                <div className="analytics-metric-card">
                  <div className="analytics-metric-value">
                    {attendanceData?.chartData?.[0]?.Attended ?? 0}
                  </div>
                  <div className="analytics-metric-label">Attended</div>
                </div>
              </div>
            </div>
          ) : (
            <div className="no-events">
              No attendance data available for this event.
            </div>
          )}
        </div>
        
        {/* Tickets Sold Chart */}
        <div className="chart-card">
          <div className="chart-header">
            <FaTicketAlt className="chart-icon" />
            <h3>Tickets Sold Analysis</h3>
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart
              data={ticketData}
              margin={{
                top: 20,
                right: 30,
                left: 20,
                bottom: 5,
              }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="event" />
              <YAxis />
              <Tooltip content={<CustomTooltip />} />
              <Legend />
              <Line type="monotone" dataKey="ticketsSold" stroke="#3a7bd5" activeDot={{ r: 8 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}

export default Analytics;

